import { useEffect, useRef } from "react";
import { HubConnectionBuilder, HttpTransportType, LogLevel } from "@microsoft/signalr";
import { useRouter } from "@/compat/navigation";
import { apiJson } from "@/lib/api/client";
import { getApiBase } from "@/lib/env";
import { notify } from "@/lib/notify";
import { useAuthStore } from "@/stores/auth-store";
import { useAdminAccessStore } from "@/stores/admin-access-store";
import {
  CUSTOMER_VOICE_ALERT_ENABLED_KEY,
  CUSTOMER_VOICE_SETTINGS_CHANGED_EVENT,
  CUSTOMER_VOICE_SOUND_SETTING_KEY,
  dispatchCustomerVoiceNewTicket,
  ensureCustomerVoiceAudioUnlockListener,
  parseAlertEnabled,
  playCustomerVoiceAlert,
  readCustomerVoiceSoundOn,
  type CustomerVoiceRealtimePayload,
} from "@/lib/customer-voice-alert";

const TYPE_LABEL: Record<string, string> = {
  complaint: "Complaint",
  suggestion: "Suggestion",
};

function normalizeEvent(raw: CustomerVoiceRealtimePayload & Record<string, unknown>): CustomerVoiceRealtimePayload {
  return {
    id: String(raw.id ?? raw.Id ?? ""),
    ticketNo: String(raw.ticketNo ?? raw.TicketNo ?? ""),
    contactName: String(raw.contactName ?? raw.ContactName ?? ""),
    feedbackType: String(raw.feedbackType ?? raw.FeedbackType ?? ""),
    category: (raw.category ?? raw.Category ?? null) as string | null,
    timestampUtc: String(raw.timestampUtc ?? raw.TimestampUtc ?? ""),
  };
}

/** Subscribes to Customer Voice SignalR and raises toast + optional sound for new tickets (all admin pages). */
export function CustomerVoiceRealtimeListener() {
  const router = useRouter();
  const token = useAuthStore((s) => s.token);
  const accessLoaded = useAdminAccessStore((s) => s.loaded);
  const hasRight = useAdminAccessStore((s) => s.hasRight);
  const canListen = Boolean(token) && accessLoaded && hasRight("CustomerVoiceIndex");

  const soundUrlRef = useRef("");
  const alertsEnabledRef = useRef(true);
  const routerRef = useRef(router);
  routerRef.current = router;

  useEffect(() => {
    if (!canListen) return;
    ensureCustomerVoiceAudioUnlockListener();
  }, [canListen]);

  useEffect(() => {
    if (!canListen) return;

    const loadSettings = () => {
      void Promise.all([
        apiJson<{ value?: string | null }>(
          `/admin/app-settings/${encodeURIComponent(CUSTOMER_VOICE_SOUND_SETTING_KEY)}`
        ).catch(() => null),
        apiJson<{ value?: string | null }>(
          `/admin/app-settings/${encodeURIComponent(CUSTOMER_VOICE_ALERT_ENABLED_KEY)}`
        ).catch(() => null),
      ]).then(([soundRow, enabledRow]) => {
        soundUrlRef.current = soundRow?.value?.trim() ?? "";
        alertsEnabledRef.current =
          enabledRow == null ? true : parseAlertEnabled(enabledRow.value);
      });
    };

    // Missing keys return 404 until first upload/toggle — treat as defaults (beep + alerts on).
    loadSettings();

    const onSettingsChanged = () => loadSettings();
    window.addEventListener(CUSTOMER_VOICE_SETTINGS_CHANGED_EVENT, onSettingsChanged);
    return () => window.removeEventListener(CUSTOMER_VOICE_SETTINGS_CHANGED_EVENT, onSettingsChanged);
  }, [canListen]);

  useEffect(() => {
    if (!canListen || !token) return;

    const connection = new HubConnectionBuilder()
      .withUrl(`${getApiBase()}/hubs/customer-voice`, {
        accessTokenFactory: () => useAuthStore.getState().token ?? "",
        withCredentials: false,
        // Prefer WebSockets; fall back to Long Polling (same as negotiate offers).
        transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(LogLevel.Warning)
      .build();

    const onEvent = (raw: CustomerVoiceRealtimePayload & Record<string, unknown>) => {
      if (!alertsEnabledRef.current) return;

      const evt = normalizeEvent(raw);
      const typeLabel = TYPE_LABEL[evt.feedbackType] ?? evt.feedbackType;
      const who = evt.contactName?.trim() || "Anonymous";
      const href = evt.id
        ? `/customer-voice/${encodeURIComponent(evt.id)}`
        : "/customer-voice";
      notify.infoClickable(
        `New Customer Voice · ${evt.ticketNo} · ${who} (${typeLabel})`,
        () => routerRef.current.push(href),
        { hint: "Click to open ticket" }
      );

      if (readCustomerVoiceSoundOn()) playCustomerVoiceAlert(soundUrlRef.current);
      dispatchCustomerVoiceNewTicket(evt);
    };

    connection.on("customer-voice-event", onEvent);

    const join = () =>
      connection.invoke("JoinDashboard").catch(() => {
        /* server also auto-joins on connect */
      });

    connection.onreconnected(() => {
      void join();
    });

    let disposed = false;
    void connection
      .start()
      .then(() => join())
      .catch(() => {
        if (!disposed) {
          /* hub optional — list still works without realtime */
        }
      });

    return () => {
      disposed = true;
      connection.off("customer-voice-event", onEvent);
      void connection.stop();
    };
  }, [canListen, token]);

  return null;
}
